/**
 * Utility functions for file upload handling
 *
 * Contains pure functions for file validation, processing, and analysis
 * that can be used by the UploadBox component and tested independently
 */

import { FileRejection } from '@zag-js/file-upload';
import { makeLogger } from '@/utils/logger/logger.ts';
import { signalWalkFinished, signalWalkStarted } from './uploadDropSignal.ts';

const logger = makeLogger('uploadUtils');

export const MIME_TYPES = {
	XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	CSV: 'text/csv',
	PDF: 'application/pdf',
	TXT: 'text/plain',
	MARKDOWN: 'text/markdown',
	DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

	// Common image MIME types
	JPEG: 'image/jpeg',
	PNG: 'image/png',
	GIF: 'image/gif',
	WEBP: 'image/webp',
	TIFF: 'image/tiff',
	HEIC: 'image/heic',
	HEIF: 'image/heif',
};

export const SUPPORTED_IMAGE_MIME_TYPES = [
	MIME_TYPES.JPEG,
	MIME_TYPES.PNG,
	MIME_TYPES.GIF,
	MIME_TYPES.WEBP,
	MIME_TYPES.TIFF,
	MIME_TYPES.HEIC,
	MIME_TYPES.HEIF,
];

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
	'.heic': MIME_TYPES.HEIC,
	'.heif': MIME_TYPES.HEIF,
	'.tif': MIME_TYPES.TIFF,
	'.tiff': MIME_TYPES.TIFF,
	'.md': MIME_TYPES.MARKDOWN,
};

/** Extension fallbacks for formats whose browser File.type is commonly empty. */
export const HEIF_FILE_EXTENSIONS = ['.heic', '.heif'];

const getExtension = (filename: string): string => {
	const dotIndex = filename.lastIndexOf('.');
	return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : '';
};

/**
 * Returns the browser-reported MIME type, falling back to a canonical type for
 * HEIC/HEIF, TIFF, and Markdown files when the browser reports no useful type.
 */
export const getEffectiveFileMimeType = (file: Pick<File, 'name' | 'type'>): string => {
	const reportedType = file.type.toLowerCase();
	if (reportedType && reportedType !== 'application/octet-stream') return reportedType;
	return MIME_TYPE_BY_EXTENSION[getExtension(file.name)] ?? reportedType;
};

/** Adds extension alternatives understood by native and Chakra file pickers. */
export const expandAcceptedFileTypes = (acceptedTypes: string[]): string[] => {
	const expanded = [...acceptedTypes];
	for (const [extension, mimeType] of Object.entries(MIME_TYPE_BY_EXTENSION)) {
		if (acceptedTypes.includes(mimeType)) expanded.push(extension);
	}
	return Array.from(new Set(expanded));
};

/** Ensures multipart uploads carry a useful Content-Type header when the browser omits one. */
export const normalizeFileMimeType = (file: File): File => {
	// TODO(backend-heif-validation): The upload service must independently verify HEIF signatures and
	// enforce decoded-image resource limits. This browser compatibility fallback is not validation.
	const mimeType = getEffectiveFileMimeType(file);
	if (!mimeType || mimeType === file.type.toLowerCase()) return file;
	return new File([file], file.name, { type: mimeType, lastModified: file.lastModified });
};

export type FileInfo = {
	name: string;
	size: number;
	type: string;
	isDirectory?: boolean;
};

/**
 * Bookkeeping files the OS writes into folders for itself. They ride along in a folder drag while
 * staying invisible in Finder and Explorer, so they are excluded before validation rather than
 * rejected by it — an unsupported-type error blames the user for a file they never chose.
 */
const SYSTEM_METADATA_FILENAMES = new Set(['.ds_store', '.localized', 'thumbs.db', 'desktop.ini']);

/** macOS writes an `._name` twin beside a file on volumes without resource-fork support. */
const APPLE_DOUBLE_PREFIX = '._';

export const isSystemMetadataFile = (filename: string): boolean =>
	SYSTEM_METADATA_FILENAMES.has(filename.toLowerCase()) || filename.startsWith(APPLE_DOUBLE_PREFIX);

export const excludeSystemMetadataFiles = (files: File[]): File[] =>
	files.filter((file) => !isSystemMetadataFile(file.name));

/**
 * Identifies the same file without reading a byte of it, from what a drop already states: name,
 * size, and when it was last written. Reading the contents is the cost this exists to avoid.
 *
 * Deliberately blind to where the file sat. A folder holding `Project Directory.pdf` at two depths
 * is holding one file, and the queue reports it as one row that is read and hashed once. The path
 * would tell the two copies apart, but nothing downstream wants them apart — the second copy has
 * no separate outcome to report, since its bytes are already accounted for.
 *
 * Two genuinely different files agreeing on all three is possible and rare; the content-hash
 * tripwire in `useHouseRecordUpload` is what would catch it.
 */
export const fileIdentity = (file: File): string => `${file.name}:${file.size}:${file.lastModified}`;

/**
 * Files from this drop that the queue does not already hold an answer for.
 *
 * A second task for a file the queue is already reporting on re-reads every byte to reach the
 * conclusion its row already states, and on a cloud-synced folder that read is the whole cost of the
 * drop. What is worth another attempt is the caller's to decide — see `isStatusWorthRetrying` and
 * the deleted-document case in `useHouseRecordUpload`.
 */
export const excludeFilesAlreadyAnsweredFor = (
	files: File[],
	taskIdByFile: ReadonlyMap<string, string>,
	isTaskWorthRetrying: (taskId: string) => boolean,
): File[] =>
	files.filter((file) => {
		const taskId = taskIdByFile.get(fileIdentity(file));
		return taskId === undefined || isTaskWorthRetrying(taskId);
	});

/**
 * Validates files against size, type, and count limits
 *
 * Checks if files exceed maximum size, are empty, have prohibited types,
 * or if there are too many files, or if they are not in the allowed types list
 */
export const validateFiles = (
	files: File[],
	maxFileSize: number,
	maxFileCount: number,
	prohibitedTypes: string[] = [],
	allowedTypes: string[] = [],
): { validFiles: File[]; rejectedFiles: FileRejection[] } => {
	const validFiles: File[] = [];
	const rejectedFiles: FileRejection[] = [];
	const normalizedProhibitedTypes = prohibitedTypes.map((type) => type.toLowerCase());
	const normalizedAllowedTypes = allowedTypes.map((type) => type.toLowerCase());

	files.forEach((file) => {
		const errors: string[] = [];
		const fileType = getEffectiveFileMimeType(file);

		if (file.size > maxFileSize) {
			errors.push('FILE_TOO_LARGE');
		}

		if (file.size === 0) {
			errors.push('FILE_EMPTY');
		}

		// Check if file type is prohibited
		if (normalizedProhibitedTypes.length > 0 && normalizedProhibitedTypes.includes(fileType)) {
			errors.push('FILE_TYPE_NOT_ALLOWED');
		}

		// Check if file type is allowed (only if allowedTypes is provided)
		if (normalizedAllowedTypes.length > 0 && !normalizedAllowedTypes.includes(fileType)) {
			errors.push('FILE_TYPE_NOT_ALLOWED');
		}

		if (errors.length > 0) {
			rejectedFiles.push({ file, errors });
		} else {
			validFiles.push(file);
		}
	});

	// Check total file count
	if (validFiles.length > maxFileCount) {
		const excess = validFiles.splice(maxFileCount);
		excess.forEach((file) => {
			rejectedFiles.push({ file, errors: ['TOO_MANY_FILES'] });
		});
	}

	return { validFiles, rejectedFiles };
};

/**
 * Extracts file information from DataTransferItemList during drag events
 *
 * Uses items instead of files since files is only available on drop
 */
export const extractFileInfo = (items: DataTransferItemList): FileInfo[] => {
	const fileInfo: FileInfo[] = [];

	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		if (item.kind === 'file') {
			const entry = item.webkitGetAsEntry();
			// Try to get the file object during drag if possible
			const file = item.getAsFile();

			fileInfo.push({
				// Use file.name if available, otherwise use a generic name
				name: file?.name || 'File',
				size: file?.size || 0,
				type: item.type,
				isDirectory: entry?.isDirectory || false,
			});
		}
	}

	return fileInfo;
};

export const MAXIMUM_AMOUNT_OF_FILES = 500;

/**
 * Analyzes dragged files for validation issues and user feedback
 */
export const analyzeFiles = (fileInfo: FileInfo[]) => {
	const tooManyFiles = fileInfo.length > MAXIMUM_AMOUNT_OF_FILES;
	const hasDirectories = fileInfo.some((file) => file.isDirectory);

	return {
		totalFiles: fileInfo.length,
		tooManyFiles,
		hasIssues: tooManyFiles,
		hasDirectories,
	};
};

/**
 * Recursively processes directory entries to extract all files
 *
 * Handles both FileSystemFileEntry and FileSystemDirectoryEntry
 * to support drag and drop of mixed files and folders
 *
 * Improved error handling to ensure files are not lost during processing
 */
export const processDirectoryEntry = async (entry: FileSystemEntry): Promise<File[]> => {
	return new Promise((resolve) => {
		if (entry.isFile) {
			const fileEntry = entry as FileSystemFileEntry;
			fileEntry.file(
				(file) => {
					logger.debug(`Successfully processed file entry: ${file.name}`);
					resolve([file]);
				},
				(error) => {
					logger.warn(`Error getting file from entry: ${error}`);
					// Instead of rejecting, resolve with empty array to continue processing other files
					resolve([]);
				},
			);
		} else if (entry.isDirectory) {
			const directoryEntry = entry as FileSystemDirectoryEntry;
			const reader = directoryEntry.createReader();
			const files: File[] = [];

			const readEntries = () => {
				reader.readEntries(
					async (entries) => {
						if (entries.length === 0) {
							logger.debug(`Finished reading directory with ${files.length} files`);
							resolve(files);
							return;
						}

						try {
							// Resolved together rather than one after another. Each `file()` call is a
							// round trip to the filesystem layer, and on a cloud-synced volume that is
							// latency spent waiting — the same reason hashing overlaps its reads. Walked
							// serially, a folder of any size holds the queue off screen for the sum of
							// them. `readEntries` hands back at most 100 at a time, which bounds the fan-out.
							const entryResults = await Promise.all(
								entries.map((entry) =>
									processDirectoryEntry(entry).catch((entryError) => {
										// Logged but not fatal: one unreadable entry must not lose the rest.
										logger.warn(`Error processing entry in directory: ${entryError}`);
										return [] as File[];
									}),
								),
							);
							for (const entryFiles of entryResults) files.push(...entryFiles);
							readEntries(); // Continue reading if there are more entries
						} catch (error) {
							logger.warn(`Error in readEntries: ${error}`);
							// Resolve with files collected so far instead of rejecting
							resolve(files);
						}
					},
					(readError) => {
						logger.warn(`Error reading directory entries: ${readError}`);
						// Resolve with files collected so far instead of rejecting
						resolve(files);
					},
				);
			};

			readEntries();
		} else {
			logger.debug(`Entry is neither file nor directory`);
			resolve([]);
		}
	});
};

/**
 * Processes dropped items to extract files from both files and directories
 *
 * Uses the FileSystem API to recursively read directory contents
 * For files, it first tries to get the file directly using getAsFile()
 * For directories, it uses the FileSystem API to recursively read contents
 *
 * Fixed to properly handle a mix of files and directories
 */
export const processDroppedItems = async (items: DataTransferItemList): Promise<File[]> => {
	// Announced here rather than at each drop handler so the queue's reading state covers exactly
	// this span, however many surfaces accept a drop. Nothing can be shown until it finishes — the
	// queue needs a file list before it can build a row — so it is the one part of a drop the user
	// would otherwise wait through with no feedback at all.
	signalWalkStarted();
	try {
		return await walkDroppedItems(items);
	} finally {
		signalWalkFinished();
	}
};

const walkDroppedItems = async (items: DataTransferItemList): Promise<File[]> => {
	const startedAt = performance.now();
	const allFiles: File[] = [];
	const directoryPromises: Promise<File[]>[] = [];

	// First pass: collect all direct files
	for (let i = 0; i < items.length; i++) {
		const item = items[i];

		if (item.kind === 'file') {
			// Get the file directly - this is the most reliable method for files
			const file = item.getAsFile();
			const entry = item.webkitGetAsEntry();

			// If it's a regular file, add it immediately
			if (file && (!entry || !entry.isDirectory)) {
				logger.debug(`Adding direct file: ${file.name}`);
				allFiles.push(file);
			}

			// If it's a directory, queue it for processing
			if (entry && entry.isDirectory) {
				logger.debug(`Queueing directory for processing: ${entry.name}`);
				directoryPromises.push(
					processDirectoryEntry(entry).catch((error) => {
						logger.warn('Error processing directory entry:', error);
						return [];
					}),
				);
			}
		}
	}

	// Second pass: process all directories in parallel
	if (directoryPromises.length > 0) {
		logger.debug(`Processing ${directoryPromises.length} directories...`);
		const directoryResults = await Promise.all(directoryPromises);

		// Add all files from directories
		for (const files of directoryResults) {
			allFiles.push(...files);
		}
	}

	// A folder walk surfaces files Finder and Explorer hide, so the OS's own bookkeeping never
	// reaches a caller — it is not part of what the user dragged.
	const droppedFiles = excludeSystemMetadataFiles(allFiles);
	const excludedCount = allFiles.length - droppedFiles.length;
	if (excludedCount > 0) {
		logger.debug(`processDroppedItems: Excluded ${excludedCount} system metadata files`);
	}
	logger.info(
		`Walked the drop: ${droppedFiles.length} files in ${Math.round(performance.now() - startedAt)}ms — ` +
			`nothing is on screen until this returns`,
	);

	return droppedFiles;
};

/**
 * Normalizes file extensions and applies alias translations.
 *
 * Why: Some users upload files with uncommon extensions (e.g., .text) while
 * downstream systems expect the conventional form (e.g., .txt). This function
 * standardizes extensions (lowercase) and translates known aliases to ensure
 * consistent, backend-friendly filenames without changing MIME type.
 */
export const normalizeFileExtensions = (files: File[]): File[] => {
	// Map of normalized (lowercased) extensions that should be translated
	const EXTENSION_ALIASES: Record<string, string> = {
		'.text': '.txt',
		// Future-safe spot for other aliases if needed, e.g.:
		// ".jpeg": ".jpg",
		// ".htm": ".html",
	};

	return files.map((file) => {
		const originalName = file.name;
		const lastDotIndex = originalName.lastIndexOf('.');

		// If no extension, return the original file
		if (lastDotIndex === -1) return file;

		const base = originalName.substring(0, lastDotIndex);
		const extLower = originalName.substring(lastDotIndex).toLowerCase();

		// Lowercase, then alias
		const newExt = EXTENSION_ALIASES[extLower] ?? extLower;
		const normalizedName = base + newExt;

		// Only recreate if the name changed
		if (normalizedName !== originalName) {
			// `lastModified` defaults to now when omitted, which would give this file a fresh identity
			// on every drop — it would hash again and never recognise the row it already has.
			return new File([file], normalizedName, { type: file.type, lastModified: file.lastModified });
		}
		return file;
	});
};

// ================= File type helpers (centralized) =================

/**
 * Supported image file extensions. Keep in sync with SUPPORTED_IMAGE_MIME_TYPES.
 */
export const IMAGE_EXTENSIONS = [
	'.png',
	'.jpg',
	'.jpeg',
	'.webp',
	'.gif',
	'.bmp',
	'.svg',
	'.tif',
	'.tiff',
	...HEIF_FILE_EXTENSIONS,
];

const NON_BROWSER_PREVIEWABLE_IMAGE_EXTENSIONS = new Set(['.heic', '.heif', '.tif', '.tiff']);
const NON_BROWSER_PREVIEWABLE_IMAGE_MIME_TYPES = new Set([
	MIME_TYPES.HEIC,
	MIME_TYPES.HEIF,
	MIME_TYPES.TIFF,
	'image/heic-sequence',
	'image/heif-sequence',
]);

/**
 * Returns true when the mimeType is an image. Uses SUPPORTED_IMAGE_MIME_TYPES
 * and also accepts any vendor 'image/*' as a safe default.
 */
export const isImageMime = (mimeType?: string): boolean => {
	if (!mimeType) return false;
	const lower = mimeType.toLowerCase();
	return lower.startsWith('image/') || SUPPORTED_IMAGE_MIME_TYPES.includes(lower);
};

/**
 * Returns true when the mimeType is a PDF.
 */
export const isPdfMime = (mimeType?: string): boolean => {
	if (!mimeType) return false;
	const lower = mimeType.toLowerCase();
	return lower === MIME_TYPES.PDF || lower.includes('pdf');
};

/**
 * Checks if the filename has a known image extension.
 */
export const hasImageExtension = (filename?: string): boolean => {
	const name = (filename || '').toLowerCase();
	return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/**
 * Generic detectors combining name and mime type.
 */
export const isImageByNameOrMime = (filename?: string, mimeType?: string): boolean => {
	return isImageMime(mimeType) || hasImageExtension(filename);
};

// TODO(file-preview-support): Add preview rendering or conversion for HEIC, HEIF, TIF, TIFF, DOCX, XLSX,
// and other supported files that browsers cannot render natively. Until then, keep them out of the <img> preview path.
export const isBrowserPreviewableImageByNameOrMime = (filename?: string, mimeType?: string): boolean => {
	const extension = getExtension(filename ?? '');
	if (NON_BROWSER_PREVIEWABLE_IMAGE_EXTENSIONS.has(extension)) return false;

	const normalizedMimeType = mimeType?.toLowerCase();
	if (normalizedMimeType && NON_BROWSER_PREVIEWABLE_IMAGE_MIME_TYPES.has(normalizedMimeType)) return false;

	return isImageByNameOrMime(filename, mimeType);
};

export const isPdfByNameOrMime = (filename?: string, mimeType?: string): boolean => {
	const name = (filename || '').toLowerCase();
	return isPdfMime(mimeType) || name.endsWith('.pdf');
};
